import { useValidators } from "@/shared/utils/validate";
import { ErrorDialogContext, errorHandledAsync, errorHandledSync, } from "@/shared/context/error-context";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { useResetPassStates } from "@/features/user/hooks/use-reset-pass-states";
import { ResetUserPassService } from "@/features/user/services/reset-pass-service";
import { MoneyLinkException } from "@/shared/utils/custom-exception";
import { ErrorType } from "@/shared/config/error-type";
import { EndpointPathNames } from "@/shared/config/endpoint-path";
import { useLoginContext } from "@/features/auth/contexts/login-context";

// ログイン画面で利用している「パスワードを忘れた方、お困りの方はこちら」
export const useResetPass = () => {
  const resetUserPassService = new ResetUserPassService();
  const navigate = useNavigate();

  const {
    email,
    setEmail,
  } = useLoginContext();

  const {
    emailError,
    setEmailError,
    isActiveSendMailButton,
    setIsActiveSendMailButton,
    isShowErrorDialog, // エラーダイアログ表示フラグ情報
    setIsShowErrorDialog,
  } = useResetPassStates();

  // メールアドレスバリデーション
  const { mailAddressValidator } = useValidators();
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);
  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  // 送信ボタン活性化チェック
  const changeSendMailButtonState = () => {
    setIsActiveSendMailButton(email !== "");
  };

  // メールアドレス変更イベント
  const onChangeMailAddress = (value: string) => {
    setEmail(value);
  };

  // メール送信ボタン押下イベント
  const onClickSendMail = async () => {
    loadingCtx.setIsLoading(true);

    // 入力されたメールアドレスをlocalStorageに保存
    localStorage.setItem("resetPassEmail", email);
    try {
      // 入力チェック
      const mailAddressErrorMessage = mailAddressValidator(email);
      if (mailAddressErrorMessage) {
        // バリデーションエラーの場合、処理終了
        setEmailError(mailAddressErrorMessage);
        return;
      }
      // 認証メール送信API実行
      await resetUserPassService.sendResetUserPassMail({
        owner_id: email
      });

      navigateResetPassDone();
    } catch (error) {
      if (error instanceof MoneyLinkException) {
        if (error.error.props.reason === ErrorType.ACCOUNTS_CLIENT_PARAMETER_ERROR) {
          // 強制的にリロードさせる
          window.location.reload();
        } else {
          throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
        }
      } else {
        throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
      }
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 入力された値とエラーメッセージをリセット
  const resetForm = () => {
    setEmail("");
    setEmailError("");
  };

  // 仮登録完了画面へ遷移
  const navigateResetPassDone = () => {
    // ブラウザバックで戻れないよう履歴を置換する
    navigate(EndpointPathNames.RESET_PASS_DONE, { replace: true });
  };

  // エラーダイアログを閉じる
  const closeErrorDialog = () => {
    setIsShowErrorDialog({
      isError: false,
      errorMessage: "",
    });
  };

  return {
    email,
    emailError,
    isActiveSendMailButton,
    isShowErrorDialog,
    onChangeMailAddress: errorHandledSync(onChangeMailAddress, setError),
    onClickSendMail: errorHandledAsync(onClickSendMail, setError),
    changeSendMailButtonState: errorHandledSync(changeSendMailButtonState, setError),
    resetForm: errorHandledSync(resetForm, setError),
    closeErrorDialog: errorHandledSync(closeErrorDialog, setError),
  };
};
